import { useEffect, useMemo, useRef, useState } from "react";
import type { GearItem, GearListItem } from "../../../api/client";
import { categorize, type CategoryData } from "../../../lib/categories";
import { exportCsv } from "../../../lib/csv";
import { UNITS, fmt, inUnit, roundTo, unitById } from "../../../lib/units";
import { ConfirmationBox } from "./ConfirmationBox";
import { ImportCsvView } from "./ImportCsvView";
import { LinkView } from "./LinkView";
import { QuickListView } from "./QuickListView";
import { ReportView } from "./ReportView";
import { UnitPicker } from "./UnitPicker";

/** One grid row: a library item, or a list line with its item. */
export type GridLine = {
  key: string;
  item: GearItem;
  line: GearListItem | null;
  quantity: number;
  worn: boolean;
  consumable: boolean;
  checked: boolean;
};

export type GridMode = "library" | "list" | "readonly";

export type GridActions = {
  updateItem: (id: string, fields: Partial<GearItem>) => void;
  updateLine: (line: GearListItem, fields: Partial<Pick<GearListItem, "quantity" | "worn" | "checked" | "consumable">>) => void;
  addItem: () => void;
  deleteRows: (rows: GridLine[]) => void;
  addToList: (itemId: string) => void;
  importRows: (rows: Partial<GearItem>[]) => void;
};

type Column = {
  key: string;
  name: string;
  width: string;
  sortable: boolean;
  editable: "auto" | "manual" | "no";
};

const COLUMNS: Record<string, Column> = {
  checked: { key: "checked", name: "", width: "15px", sortable: true, editable: "no" },
  name: { key: "name", name: "Name", width: "245px", sortable: true, editable: "manual" },
  description: { key: "description", name: "Description", width: "", sortable: true, editable: "manual" },
  url: { key: "url", name: "", width: "17px", sortable: true, editable: "no" },
  category: { key: "category", name: "Category", width: "75px", sortable: true, editable: "auto" },
  quantity: { key: "quantity", name: "qty", width: "17px", sortable: true, editable: "auto" },
  grams: { key: "grams", name: "Weight", width: "80px", sortable: true, editable: "auto" },
  worn: { key: "worn", name: "", width: "17px", sortable: true, editable: "no" },
  consumable: { key: "consumable", name: "", width: "17px", sortable: true, editable: "no" },
};

function columnsFor(mode: GridMode): Column[] {
  const base = ["name", "description", "url", "category", "grams", "worn", "consumable"];
  const keys = mode === "list" ? ["checked", "name", "description", "url", "category", "quantity", "grams", "worn", "consumable"] : base;
  return keys.map((k) => COLUMNS[k]);
}

function sortValue(l: GridLine, key: string): string | number | boolean {
  switch (key) {
    case "checked":
      return l.checked;
    case "name":
      return l.item.name.toLowerCase();
    case "description":
      return l.item.description.toLowerCase();
    case "url":
      return l.item.url !== "";
    case "category":
      return l.item.category.toLowerCase() + "\u0000" + l.item.name.toLowerCase();
    case "quantity":
      return l.quantity;
    case "grams":
      return l.item.grams;
    case "worn":
      return l.worn;
    case "consumable":
      return l.consumable;
    default:
      return "";
  }
}

export function GearListView({
  title,
  mode,
  lines,
  libraryItems,
  unitId,
  onUnitChange,
  onSelectionChange,
  actions,
  shareUrl,
}: {
  title: string;
  mode: GridMode;
  lines: GridLine[];
  libraryItems: GearItem[];
  unitId: number;
  onUnitChange: (id: number) => void;
  onSelectionChange?: (rows: GridLine[]) => void;
  actions: GridActions | null;
  shareUrl?: string;
}) {
  const columns = columnsFor(mode);
  const [sort, setSort] = useState<{ col: number; dir: 1 | -1 }>({ col: mode === "list" ? 1 : 0, dir: 1 });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<{ key: string; col: string } | null>(null);
  const [menu, setMenu] = useState<{ kind: "unit" | "category"; key: string; top: number; left: number } | null>(null);
  const [showReport, setShowReport] = useState(false);
  const [showQuick, setShowQuick] = useState(false);
  const [showLink, setShowLink] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const holderRef = useRef<HTMLDivElement>(null);
  const editRef = useRef<HTMLDivElement>(null);
  const readonly = mode === "readonly" || !actions;
  const unit = unitById(unitId);

  const sorted = useMemo(() => {
    const col = columns[sort.col];
    if (!col) return lines;
    return [...lines].sort((a, b) => {
      const av = sortValue(a, col.key);
      const bv = sortValue(b, col.key);
      if (av < bv) return -sort.dir;
      if (av > bv) return sort.dir;
      return 0;
    });
  }, [lines, sort, columns]);

  const categories: CategoryData[] = useMemo(
    () => categorize(lines.map((l) => ({ category: l.item.category, grams: l.item.grams, quantity: l.quantity, worn: l.worn, consumable: l.consumable }))),
    [lines],
  );
  const catClass = (name: string) => categories.find((c) => c.name === name)?.cssClass ?? "";
  const totals = useMemo(() => {
    let total = 0;
    let worn = 0;
    let consumable = 0;
    for (const l of lines) {
      const w = l.item.grams * l.quantity;
      total += w;
      if (l.worn) worn += w;
      if (l.consumable) consumable += w;
    }
    return { total, worn, consumable };
  }, [lines]);
  const categoryList = useMemo(
    () => [...new Set(libraryItems.map((i) => i.category))].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())),
    [libraryItems],
  );

  useEffect(() => {
    onSelectionChange?.(sorted.filter((l) => selected.has(l.key)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, lines]);

  // switching lists keeps the report/list choice; only the selection and edit state reset
  useEffect(() => {
    setSelected(new Set());
    setEditing(null);
  }, [title, mode]);

  useEffect(() => {
    if (editing && editRef.current) {
      editRef.current.focus();
      const range = document.createRange();
      range.selectNodeContents(editRef.current);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  }, [editing]);

  useEffect(() => {
    const close = () => setMenu(null);
    document.addEventListener("mouseup", close);
    return () => document.removeEventListener("mouseup", close);
  }, []);

  function onRowClick(e: React.MouseEvent, line: GridLine) {
    if (editing) return;
    const next = new Set(e.metaKey || e.ctrlKey || e.shiftKey ? selected : []);
    if (next.has(line.key) && (e.metaKey || e.ctrlKey)) next.delete(line.key);
    else next.add(line.key);
    setSelected(next);
  }

  function commitEdit(line: GridLine, col: string, raw: string) {
    setEditing(null);
    if (!actions) return;
    const value = raw.replace(/\s+/g, " ").trim();
    switch (col) {
      case "name":
      case "description":
      case "category":
        if (value !== (line.item as unknown as Record<string, string>)[col]) actions.updateItem(line.item.id, { [col]: value });
        break;
      case "quantity": {
        const q = parseFloat(value);
        if (line.line && Number.isFinite(q) && q >= 0 && q !== line.quantity) actions.updateLine(line.line, { quantity: q });
        break;
      }
      case "grams": {
        const v = parseFloat(value);
        if (Number.isFinite(v) && v >= 0) {
          const grams = v * unitById(line.item.unit_id).grams;
          if (grams !== line.item.grams) actions.updateItem(line.item.id, { grams });
        }
        break;
      }
    }
  }

  function toggleFlag(line: GridLine, flag: "worn" | "consumable" | "checked") {
    if (!actions) return;
    if (mode === "list" && line.line) actions.updateLine(line.line, { [flag]: !line[flag] });
    else if (flag !== "checked") actions.updateItem(line.item.id, { [flag]: !line.item[flag] });
  }

  function cellValue(line: GridLine, col: Column): string {
    switch (col.key) {
      case "name":
        return line.item.name;
      case "description":
        return line.item.description;
      case "category":
        return line.item.category;
      case "quantity":
        return String(line.quantity);
      case "grams": {
        const u = unitById(line.item.unit_id);
        const w = roundTo(line.item.grams / u.grams, 2);
        return readonly ? `${w}${u.abbreviation}` : String(w);
      }
      default:
        return "";
    }
  }

  function openMenu(e: React.MouseEvent, kind: "unit" | "category", line: GridLine) {
    e.stopPropagation();
    const cell = (e.currentTarget as HTMLElement).closest("td")!;
    const rect = cell.getBoundingClientRect();
    const holder = holderRef.current?.getBoundingClientRect();
    setMenu(menu?.key === line.key && menu.kind === kind ? null : { kind, key: line.key, top: rect.bottom - (holder?.top ?? 0) + (holderRef.current?.scrollTop ?? 0), left: rect.left - (holder?.left ?? 0) });
  }

  function exportCSV() {
    const rows = lines.map((l) => ({
      name: l.item.name,
      description: l.item.description,
      url: l.item.url,
      category: l.item.category,
      grams: l.item.grams,
      unit_id: l.item.unit_id,
      quantity: mode === "list" ? l.quantity : null,
      worn: l.worn,
      consumable: l.consumable,
    }));
    const blob = new Blob([exportCsv(rows, mode === "list")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${title || "gear"}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  const selectedRows = sorted.filter((l) => selected.has(l.key));
  const menuLine = menu ? lines.find((l) => l.key === menu.key) : null;

  return (
    <div className="GearListView shown" style={{ whiteSpace: "nowrap" }}>
      {mode === "list" && actions && (
        <QuickListView shown={showQuick} items={libraryItems} usedItemIds={new Set(lines.map((l) => l.item.id))} onAdd={actions.addToList} />
      )}
      <div
        className="dataGridHolder"
        ref={holderRef}
        style={{ position: "absolute", top: 0, bottom: 25, left: showQuick && mode === "list" ? 200 : 0, right: 0, overflowY: "scroll" }}
        onScroll={() => setMenu(null)}
      >
        <table className="DataGrid" onContextMenu={(e) => e.preventDefault()}>
          <thead>
            <tr>
              {columns.map((col, i) => (
                <th
                  key={col.key}
                  style={{ width: col.width || undefined }}
                  className={col.sortable ? "sortableColumn" : ""}
                  onClick={() => col.sortable && setSort((s) => ({ col: i, dir: s.col === i ? ((-s.dir) as 1 | -1) : 1 }))}
                >
                  <span className="columnName">{col.name}</span>
                  {sort.col === i && (
                    <div className="sortButtonHolder">
                      <a className={`sortButton ${sort.dir === 1 ? "up" : "down"}`} href="#sort" onClick={(e) => e.preventDefault()}>
                        {sort.dir === 1 ? "+" : "-"}
                      </a>
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((line) => (
              <tr
                key={line.key}
                className={selected.has(line.key) ? "selectedRow" : ""}
                draggable={mode === "library" && !readonly}
                onDragStart={(e) => {
                  const ids = selected.has(line.key) ? selectedRows.map((l) => l.item.id) : [line.item.id];
                  e.dataTransfer.setData("text/gg-items", ids.join(","));
                }}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`${col.key} ${col.key === "category" ? catClass(line.item.category) : ""} ${editing?.key === line.key && editing.col === col.key ? "editing" : ""}`}
                    onMouseUp={(e) => {
                      if (col.editable === "auto" && !readonly && !(editing?.key === line.key && editing.col === col.key)) {
                        onRowClick(e, line);
                        setEditing({ key: line.key, col: col.key });
                      } else if (col.editable !== "auto" || readonly) onRowClick(e, line);
                    }}
                    onDoubleClick={() => col.editable === "manual" && !readonly && setEditing({ key: line.key, col: col.key })}
                  >
                    {col.key === "checked" && line.line && (
                      <>
                        <input
                          type="checkbox"
                          className="checkedBox"
                          title="Checked"
                          checked={line.checked}
                          onChange={() => toggleFlag(line, "checked")}
                          onMouseUp={(e) => e.stopPropagation()}
                        />
                        <label onClick={() => toggleFlag(line, "checked")} />
                      </>
                    )}
                    {col.key === "url" && (
                      <>
                        <input
                          type="checkbox"
                          className="urlBox"
                          title={line.item.url ? line.item.url : "Add a link"}
                          checked={line.item.url !== ""}
                          readOnly
                          onMouseUp={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.preventDefault();
                            if (line.item.url) window.open(line.item.url, "_blank");
                            else if (!readonly) {
                              const url = window.prompt("Add a link for this item", "https://");
                              if (url && url !== "https://") actions?.updateItem(line.item.id, { url });
                            }
                          }}
                        />
                        <label />
                      </>
                    )}
                    {(col.key === "worn" || col.key === "consumable") && (
                      <>
                        <input
                          type="checkbox"
                          className={col.key}
                          title={col.key === "worn" ? "Worn Item" : "Consumable Item"}
                          checked={line[col.key]}
                          disabled={readonly}
                          onChange={() => toggleFlag(line, col.key as "worn" | "consumable")}
                          onMouseUp={(e) => e.stopPropagation()}
                        />
                        <label title={col.key === "worn" ? "Worn Item" : "Consumable Item"} onClick={() => !readonly && toggleFlag(line, col.key as "worn" | "consumable")} />
                      </>
                    )}
                    {["name", "description", "category", "quantity", "grams"].includes(col.key) && (
                      <>
                        <div
                          ref={editing?.key === line.key && editing.col === col.key ? editRef : undefined}
                          title={cellValue(line, col)}
                          className={`editable ${col.editable === "manual" ? "manual" : ""}`}
                          spellCheck={false}
                          contentEditable={editing?.key === line.key && editing.col === col.key}
                          suppressContentEditableWarning
                          onBlur={(e) => editing?.key === line.key && editing.col === col.key && commitEdit(line, col.key, e.currentTarget.textContent ?? "")}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              commitEdit(line, col.key, e.currentTarget.textContent ?? "");
                            } else if (e.key === "Escape") {
                              e.currentTarget.textContent = cellValue(line, col);
                              setEditing(null);
                            } else if ((col.key === "grams" || col.key === "quantity") && e.key.length === 1 && !/[0-9.]/.test(e.key)) e.preventDefault();
                          }}
                        >
                          {cellValue(line, col)}
                        </div>
                        {col.key === "grams" && !readonly && (
                          <div className="unitHolder" onClick={(e) => openMenu(e, "unit", line)} onMouseUp={(e) => e.stopPropagation()}>
                            <span className="weightUnitDisplay">{unitById(line.item.unit_id).abbreviation}</span>
                            <i className="icon_expand" />
                          </div>
                        )}
                        {(col.key === "name" || col.key === "description") && !readonly && (
                          <a
                            title="Edit"
                            className="editFieldButton"
                            href="#edit"
                            onMouseUp={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setEditing({ key: line.key, col: col.key });
                            }}
                          >
                            E
                          </a>
                        )}
                        {col.key === "category" && !readonly && (
                          <a
                            title="Pick a category"
                            className="editFieldButton"
                            href="#cat"
                            onMouseUp={(e) => {
                              e.preventDefault();
                              openMenu(e, "category", line);
                            }}
                          >
                            E
                          </a>
                        )}
                      </>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {menu && menuLine && (
          <ul
            className={`dataSelect ${menu.kind === "unit" ? "weightUnitSelect" : "categorySelect"}`}
            style={{ visibility: "visible", top: menu.top, left: menu.left, marginLeft: 0, marginTop: 0 }}
            onMouseUp={(e) => e.stopPropagation()}
            role="menu"
          >
            {menu.kind === "unit"
              ? UNITS.map((u) => (
                  <li
                    key={u.id}
                    onMouseUp={() => {
                      actions?.updateItem(menuLine.item.id, { unit_id: u.id });
                      setMenu(null);
                    }}
                  >
                    {u.abbreviation}
                  </li>
                ))
              : categoryList.map((c) => (
                  <li
                    key={c || "(none)"}
                    onMouseUp={() => {
                      actions?.updateItem(menuLine.item.id, { category: c });
                      setMenu(null);
                    }}
                  >
                    {c || "None"}
                  </li>
                ))}
          </ul>
        )}
      </div>

      {showReport && <ReportView title={title} lines={lines} unitId={unitId} onUnitChange={onUnitChange} />}

      <div className="controls">
        <div className="leftControls">
          {mode === "list" && !readonly && (
            <a className={`controlButton controlItem quickListButton${showQuick ? " shown" : ""} shown`} href="#quick" title="All Gear" onClick={(e) => (e.preventDefault(), setShowQuick(!showQuick))}>
              L
            </a>
          )}
          {!readonly && (
            <>
              <a className="controlButton controlItem" href="#add" title="Add Item" onClick={(e) => (e.preventDefault(), actions?.addItem())}>
                +
              </a>
              <a className="controlButton" href="#del" title="Delete Selected Items" onClick={(e) => (e.preventDefault(), selectedRows.length && setConfirmDelete(true))}>
                -
              </a>
            </>
          )}
          <a className="controlButton controlItem exportCSV" href="#export" title="Export CSV" onClick={(e) => (e.preventDefault(), exportCSV())}>
            +
          </a>
          {!readonly && (
            <a className="controlButton controlItem importCSV" href="#import" title="Import CSV" onClick={(e) => (e.preventDefault(), setShowImport(true))}>
              +
            </a>
          )}
          {shareUrl && (
            <a className="controlButton controlItem copyListURLButton" href="#share" title="Share" style={{ display: "inline-block" }} onClick={(e) => (e.preventDefault(), setShowLink(!showLink))}>
              +
            </a>
          )}
          <a className="controlButton controlItem printButton" href="#print" title="Print" onClick={(e) => (e.preventDefault(), setShowReport(true), setTimeout(() => window.print(), 50))}>
            +
          </a>
        </div>
        <div className="rightControls">
          <div className="listTotal">Total: {inUnit(totals.total, unit)}</div>
          <div className="controlItem">
            <UnitPicker value={unitId} onChange={onUnitChange} />
          </div>
          <div className="packTotal">Pack: {fmt(totals.total - totals.worn, unit)}</div>
          <div className="wornTotal">{fmt(totals.worn, unit)}</div>
          <div className="consumableTotal">{fmt(totals.consumable, unit)}</div>
          <a className="controlButton reportButton" href="#report" title={showReport ? "Show List" : "Show Report"} onClick={(e) => (e.preventDefault(), setShowReport(!showReport))}>
            {showReport ? "List" : "Report"}
          </a>
        </div>
      </div>

      {showLink && shareUrl && <LinkView url={shareUrl} onClose={() => setShowLink(false)} />}
      {showImport && actions && (
        <ImportCsvView
          onCancel={() => setShowImport(false)}
          onImport={(rows) => {
            setShowImport(false);
            actions.importRows(rows);
          }}
        />
      )}
      {confirmDelete && (
        <ConfirmationBox
          heading="Delete Items"
          message={mode === "list" ? "Remove the selected items from this list?" : "Are you sure you want to delete the selected items?"}
          confirmLabel="Delete"
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => {
            setConfirmDelete(false);
            actions?.deleteRows(selectedRows);
            setSelected(new Set());
          }}
        />
      )}
    </div>
  );
}
